/**
 * FlowMention hook — @ autocomplete for the Flow Builder chat
 * (`SeivaWeb.Live.Schedules.Components.FlowChat`).
 *
 *   @ToolName    — workspace tool (info badge)
 *   @ModelName   — LLM model from the catalog (info badge)
 *   @AgentName   — workspace agent (amber badge)
 *   @UserName    — workspace member (slate badge)
 *
 * Mirrors `chat_mention.js` (App Builder chat) wholesale — same dropdown
 * lifecycle (body-mounted with `position: fixed`), same `.cm-item` class
 * names so the global CSS (`.cm-item.cm-sel { … }`, `.cm-item:hover { … }`)
 * applies, same key handling. Drop-in compatible. Differences are limited
 * to the candidate types (tools/models/agents/users) and their data
 * attribute names (`flow-mention-*` instead of `mention-*`).
 *
 * Candidates come exclusively from data attributes on the textarea —
 * `SchedulesLive.preload_flow_chat/1` populates them server-side and the
 * LiveView re-renders on every assigns change. No `pushEvent` round-trip.
 */
// Mention accent colors as CSS custom properties. They land in inline-style
// strings (overlay highlight + dropdown badges), where `var(--token)` resolves
// natively — so they track dark mode and partnership branding with no JS read.
const TOOL_COLOR = "var(--info)"
const MODEL_COLOR = "var(--info)"
const AGENT_COLOR = "var(--warn)"
const USER_COLOR = "var(--n-600)"

export default {
  mounted() {
    this.textarea = this.el
    this.candidates = []
    this.tools = []
    this.models = []
    this.agents = []
    this.users = []
    this.mentionStart = -1
    this.selectedIdx = 0
    this.isOpen = false
    this._skipNextBlur = false

    this._loadData()
    this._createDropdown()

    // Overlay for styled mentions behind the textarea (id set via data attr).
    // Progressive enhancement: only flip the textarea text to transparent
    // when we actually found the overlay. If it's missing in prod (stale
    // asset, hook failed before this line, etc.), the textarea keeps its
    // native color so the user still sees what they type.
    const overlayId = this.textarea.dataset.flowMentionOverlayId || "flow-chat-mention-overlay"
    this.overlay = document.getElementById(overlayId)
    if (this.overlay) {
      this.textarea.style.color = "transparent"
      this.textarea.style.webkitTextFillColor = "transparent"
    }

    this._inputHandler = () => { this._resize(); this._detectMention(); this._syncOverlay() }
    this._keydownHandler = (e) => this._onKeydown(e)
    this._blurHandler = () => {
      if (this._skipNextBlur) {
        this._skipNextBlur = false
        return
      }
      setTimeout(() => {
        if (document.activeElement !== this.textarea) this._close()
      }, 150)
    }
    this._scrollHandler = () => {
      if (this.overlay) this.overlay.scrollTop = this.textarea.scrollTop
    }

    this.textarea.addEventListener("input", this._inputHandler)
    this.textarea.addEventListener("keydown", this._keydownHandler)
    this.textarea.addEventListener("blur", this._blurHandler)
    this.textarea.addEventListener("scroll", this._scrollHandler)

    // Initial overlay sync (LV can re-mount with an already-filled value)
    this._syncOverlay()
  },

  updated() {
    // Throwing inside updated() aborts the LiveView DOM patch — keep this
    // resilient so a single bad mention candidate cannot freeze the editor.
    try {
      this._loadData()
      if (this.textarea.value) this._resize()
      this._syncOverlay()
      if (this.isOpen) this._detectMention()
    } catch (err) {
      console.error("[FlowMention] updated() error:", err)
    }
  },

  destroyed() {
    if (this.dropdown?.parentNode) this.dropdown.parentNode.removeChild(this.dropdown)
    if (this.textarea) {
      this.textarea.style.color = ""
      this.textarea.style.webkitTextFillColor = ""
      this.textarea.removeEventListener("input", this._inputHandler)
      this.textarea.removeEventListener("keydown", this._keydownHandler)
      this.textarea.removeEventListener("blur", this._blurHandler)
      this.textarea.removeEventListener("scroll", this._scrollHandler)
    }
  },

  _loadData() {
    try { this.tools = JSON.parse(this.textarea.dataset.flowMentionTools || "[]") } catch (_) { this.tools = [] }
    try { this.models = JSON.parse(this.textarea.dataset.flowMentionModels || "[]") } catch (_) { this.models = [] }
    try { this.agents = JSON.parse(this.textarea.dataset.flowMentionAgents || "[]") } catch (_) { this.agents = [] }
    try { this.users = JSON.parse(this.textarea.dataset.flowMentionUsers || "[]") } catch (_) { this.users = [] }
  },

  // Expand textarea up to `data-max-lines` (default 4); scroll afterwards.
  // Mirrors chat_mention.js _resize for a matching UX with the App Builder.
  _resize() {
    const el = this.textarea
    const maxLines = parseInt(el.dataset.maxLines || "4", 10)
    const lh = parseInt(getComputedStyle(el).lineHeight, 10) || 20
    const pt = parseInt(getComputedStyle(el).paddingTop, 10) || 0
    const pb = parseInt(getComputedStyle(el).paddingBottom, 10) || 0
    const maxH = maxLines * lh + pt + pb
    const minH = lh + pt + pb
    el.style.height = "auto"
    el.style.height = Math.max(Math.min(el.scrollHeight, maxH), minH) + "px"
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden"
    if (this.overlay) {
      this.overlay.style.height = el.style.height
      this.overlay.style.overflowY = el.style.overflowY
    }
  },

  // Build combined known mentions list with their colors for overlay
  _buildKnownMentions() {
    const mentions = []
    const push = (name, color) => {
      if (typeof name === "string" && name.length > 0) mentions.push({ name, color })
    }
    this.tools.forEach(t => push(t.name, TOOL_COLOR))
    this.models.forEach(m => push(m.name, MODEL_COLOR))
    this.agents.forEach(a => push(a.name, a.color || AGENT_COLOR))
    this.users.forEach(u => push(u.name, USER_COLOR))
    return mentions.sort((a, b) => b.name.length - a.name.length)
  },

  _syncOverlay() {
    if (!this.overlay) return
    const val = this.textarea.value
    if (!val) { this.overlay.innerHTML = ""; return }

    const esc = val.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

    const known = this._buildKnownMentions()
    let highlighted = esc
    if (known.length > 0) {
      const escaped = known.map(m => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      const re = new RegExp("@(" + escaped.join("|") + ")(?=\\s|$)", "g")
      highlighted = esc.replace(re, (_m, name) => {
        const m = known.find(k => k.name === name)
        const color = m ? m.color : "var(--accent-primary)"
        return `<span style="color:${color};font-weight:600">@${name}</span>`
      })
    }
    this.overlay.innerHTML = highlighted
    this.overlay.scrollTop = this.textarea.scrollTop
  },

  // ── Dropdown — body-mounted with `position: fixed`, exact mirror of
  // ChatMention. The shared `.cm-item` / `.cm-sel` class names hook into
  // the global CSS so hover/selected highlight works without per-hook rules.
  _createDropdown() {
    const dd = document.createElement("div")
    dd.className = "chat-mention-dropdown"
    Object.assign(dd.style, {
      display: "none", position: "fixed", zIndex: "9999",
      maxHeight: "220px", overflowY: "auto", borderRadius: "8px",
      border: "1px solid var(--border-default)", background: "var(--bg-card)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "4px 0",
    })

    dd.addEventListener("mousedown", (e) => {
      e.preventDefault()
      const item = e.target.closest(".cm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx >= 0 && idx < this.candidates.length) {
        this._skipNextBlur = true
        this._select(this.candidates[idx])
      }
    })

    dd.addEventListener("mouseover", (e) => {
      const item = e.target.closest(".cm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx !== this.selectedIdx) {
        this.selectedIdx = idx
        this._highlightSelected()
      }
    })

    document.body.appendChild(dd)
    this.dropdown = dd
  },

  _positionDropdown() {
    const rect = this.textarea.getBoundingClientRect()
    Object.assign(this.dropdown.style, {
      left: rect.left + "px",
      width: rect.width + "px",
      bottom: (window.innerHeight - rect.top + 4) + "px",
      top: "auto",
    })
  },

  _detectMention() {
    const val = this.textarea.value
    const caret = this.textarea.selectionStart
    let scanPos = caret - 1
    let foundAt = -1

    while (scanPos >= 0) {
      const ch = val[scanPos]
      if (ch === "\n") break
      if (ch === "@") {
        const before = scanPos >= 1 ? val[scanPos - 1] : " "
        if (before === " " || before === "\n" || scanPos === 0) foundAt = scanPos
        break
      }
      scanPos--
    }

    if (foundAt === -1) {
      if (this.isOpen) this._close()
      return
    }

    const query = val.slice(foundAt + 1, caret)
    this.mentionStart = foundAt
    this._filter(query)

    if (this.candidates.length > 0) {
      if (!this.isOpen) this._open()
      else { this._renderItems(); this._positionDropdown() }
    } else {
      if (this.isOpen) this._close()
    }
  },

  _onKeydown(e) {
    if (!this.isOpen || this.candidates.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.selectedIdx = Math.min(this.selectedIdx + 1, this.candidates.length - 1)
        this._highlightSelected()
        break
      case "ArrowUp":
        e.preventDefault()
        this.selectedIdx = Math.max(this.selectedIdx - 1, 0)
        this._highlightSelected()
        break
      case "Enter":
      case "Tab":
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this._select(this.candidates[this.selectedIdx])
        break
      case "Escape":
        e.preventDefault()
        this._close()
        break
    }
  },

  _filter(query) {
    const q = query.toLowerCase()

    const toolResults = this.tools
      .filter(t => (t.name || "").toLowerCase().includes(q) || (t.id || "").toLowerCase().includes(q))
      .map(t => ({ type: "tool", label: t.name, sublabel: t.description || t.id, value: t.name }))

    const agentResults = this.agents
      .filter(a => (a.name || "").toLowerCase().includes(q) || (a.id || "").toLowerCase().includes(q))
      .map(a => ({ type: "agent", label: a.name, sublabel: a.id, value: a.name, color: a.color || AGENT_COLOR }))

    const userResults = this.users
      .filter(u =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      )
      .map(u => ({ type: "user", label: u.name, sublabel: u.email || u.id, value: u.name }))

    const modelResults = this.models
      .filter(m => (m.name || "").toLowerCase().includes(q) || (m.id || "").toLowerCase().includes(q))
      .map(m => ({ type: "model", label: m.name, sublabel: m.id, value: m.name }))

    // Order: tools (executables) -> agents -> users -> models. Keeps the
    // most actionable on top while letting the user @-cite people too.
    this.candidates = [
      ...toolResults,
      ...agentResults,
      ...userResults,
      ...modelResults
    ].slice(0, 12)
    this.selectedIdx = 0
  },

  _open() {
    this.isOpen = true
    this.textarea.dataset.flowMentionOpen = "true"
    this.dropdown.style.display = "block"
    this._positionDropdown()
    this._renderItems()
  },

  _close() {
    this.isOpen = false
    delete this.textarea.dataset.flowMentionOpen
    this.dropdown.style.display = "none"
    this.mentionStart = -1
    this.candidates = []
  },

  _renderItems() {
    this.dropdown.innerHTML = this.candidates.map((c, i) => {
      const sel = i === this.selectedIdx ? " cm-sel" : ""
      let color, badge
      switch (c.type) {
        case "tool":  color = TOOL_COLOR;  badge = "T"; break
        case "model": color = MODEL_COLOR; badge = "M"; break
        case "agent": color = c.color || AGENT_COLOR; badge = "A"; break
        case "user":  color = USER_COLOR;  badge = "U"; break
        default:      color = TOOL_COLOR;  badge = "?"
      }
      const sub = c.sublabel
        ? `<span style="font-size:10px;color:var(--text-tertiary);margin-left:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">— ${this._escape(c.sublabel)}</span>`
        : ""
      return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:${color};color:white;font-size:10px;font-weight:700;flex-shrink:0">${badge}</span>
        <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${this._escape(c.label)}${sub}</span>
      </div>`
    }).join("")
  },

  _escape(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  },

  _highlightSelected() {
    this.dropdown.querySelectorAll(".cm-item").forEach((el, i) => {
      el.classList.toggle("cm-sel", i === this.selectedIdx)
    })
  },

  _select(candidate) {
    if (!candidate) return
    const val = this.textarea.value
    const before = val.slice(0, this.mentionStart)
    const afterCaret = val.slice(this.textarea.selectionStart)
    const insertion = `@${candidate.value} `
    const newVal = before + insertion + afterCaret
    const newCaret = before.length + insertion.length

    this._close()

    const nativeSetter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype, "value"
    ).set
    nativeSetter.call(this.textarea, newVal)

    this.textarea.selectionStart = newCaret
    this.textarea.selectionEnd = newCaret
    this.textarea.focus()
    this._resize()
    this._syncOverlay()
    this.textarea.dispatchEvent(new Event("input", { bubbles: true }))
  },
}
